/**
 * Groups Manager Component
 * Component منفصل فقط لإدارة Groups
 */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Trash2, Edit3, Folder, Users } from 'lucide-react';

const GroupsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid ${props => props.theme.border || '#ccc'};
  border-radius: 6px;
  background: ${props => props.theme.surface || '#fff'};
`;

const GroupInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GroupName = styled.span`
  font-size: 14px;
  color: ${props => props.theme.text || '#000'};
  font-weight: 500;
`;

const GroupDetails = styled.span`
  font-size: 12px;
  color: ${props => props.theme.textSecondary || '#666'};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 6px 12px;
  border: 1px solid ${props => props.theme.border || '#ccc'};
  border-radius: 4px;
  background: ${props => props.variant === 'danger' ? '#dc3545' : props.theme.primary || '#007bff'};
  color: white;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    opacity: 0.8;
  }
`;

export const GroupsManager = ({ project, onUpdate }) => {
  const [groups, setGroups] = useState(project.groups || []);

  useEffect(() => {
    setGroups(project.groups || []);
  }, [project.groups]);

  /**
   * Create new group
   */
  const handleCreateGroup = (name = 'New Group', partIds = []) => {
    const newGroup = {
      id: `group_${Date.now()}`,
      name,
      parts: partIds,
      segments: [],
      language: 'original',
      subtitles: null,
      watermarks: []
    };
    
    const updatedGroups = [...groups, newGroup];
    setGroups(updatedGroups);
    onUpdate(updatedGroups);
  };

  /**
   * Delete group
   */
  const handleDeleteGroup = (groupId) => {
    const updatedGroups = groups.filter(g => g.id !== groupId);
    setGroups(updatedGroups);
    onUpdate(updatedGroups);
  };

  /**
   * Update group settings
   */
  const handleUpdateGroup = (groupId, updates) => {
    const updatedGroups = groups.map(g => 
      g.id === groupId ? { ...g, ...updates } : g
    );
    setGroups(updatedGroups);
    onUpdate(updatedGroups);
  };

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '16px' 
      }}>
        <span style={{ 
          fontSize: '14px', 
          color: '#666',
          fontWeight: 500
        }}>
          {groups.length} {groups.length === 1 ? 'Group' : 'Groups'}
        </span>
        <Button 
          onClick={() => handleCreateGroup()}
          variant="primary"
          size="small"
        >
          <Plus size={14} />
          Create Group
        </Button>
      </div>
      
      <GroupsList>
        {groups.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            No groups yet. Click "Create Group" to create one.
          </div>
        ) : (
          groups.map(group => (
            <GroupItem key={group.id}>
              <GroupInfo>
                <GroupName>
                  <Folder size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  {group.name}
                </GroupName>
                <GroupDetails>
                  {group.parts?.length || 0} parts • {group.segments?.length || 0} segments
                  {group.language && group.language !== 'original' && ` • ${group.language.toUpperCase()}`}
                </GroupDetails>
              </GroupInfo>
              <Actions>
                <Button onClick={() => handleUpdateGroup(group.id, { name: 'Updated Group' })}>
                  <Edit3 size={14} />
                  Edit
                </Button>
                <Button variant="danger" onClick={() => handleDeleteGroup(group.id)}>
                  <Trash2 size={14} />
                  Delete
                </Button>
              </Actions>
            </GroupItem>
          ))
        )}
      </GroupsList>
    </div>
  );
};

